import {injectable, inject} from "inversify";
import {HttpService, HTTP_SERVICE_NAME} from "../../http/HttpService";
import {formatDate} from "../../../ui/util/date";

export interface SlotSpinHistoryService {
    listSpinHistory(req: SlotSpinHistoryRequest): Promise<Spin[]>;
    getSpinHistory(req: SlotSpinHistoryRequest): Promise<Spin[]>;
    listAllSlotGames(): Promise<SlotGame[]>
}

export interface SlotSpinHistoryRequest {
    playerId: string;
    startTime: number;
    endTime: number;
    currency: number;
    gameName: string;
    offset: number;
}

export interface Spin {
    id: number;
    userId: number;
    playId: string;
    date: string;
    gameId: string;
    currency: number;
    spinValue: number;
    winValue: number;
    freeSpins: number;
    giftSpinId: string;
}

export interface SlotGame {
    id: string;
    name: string;
    provider: string;
}

const DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

@injectable()
export class SlotSpinHistoryServiceImpl implements SlotSpinHistoryService {

    http: HttpService;

    constructor(@inject(HTTP_SERVICE_NAME) http: HttpService) {
        this.http = http;
    }

    listSpinHistory(req: SlotSpinHistoryRequest): Promise<Spin[]> {
        const path = `/slots/admin-spin-history?user_id=${encodeURI(req.playerId)}&offset=${encodeURI(String(req.offset))}`;
        return this.http.doGet(path);
    }

    getSpinHistory(req: SlotSpinHistoryRequest): Promise<Spin[]> {
        const gameIds = this.getGameIds(req.gameName)
        const path = `/slots/spins?` +
            `start_date=${encodeURI(formatDate(req.startTime, DATE_FORMAT))}&`+
            `end_date=${encodeURI(formatDate(req.endTime, DATE_FORMAT))}&` +
            `currency=${encodeURI((req.currency).toString())}&` +
            `game_name=${encodeURI(gameIds.toString())}&` +
            `user_id=${encodeURI(req.playerId)}&` +
            `offset=${encodeURI((req.offset || 0).toString())}`;
        return this.http.doGet(path);
    }

    private getGameIds(gameName: string): string {
        const GAME_ID_MAPPINGS = {
            sandsOfEternity: 'sands-of-eternity',
            thunderWheel: 'thunder-wheel',
            threeSamurai: 'three-samurai',
            fruitSmash: 'fruit-smash',
            starSpell: 'starspell',
            moneyTrain: 'moneytrain',
            clusterTumble: 'clustertumble',
            spaceMiners: 'spaceminers',
            tntTumble: 'tnttumble',
            templarTumble: 'templartumble',
            ironBank: 'ironbank',
            volatileVikings: 'volatilevikings',
            moneyTrain2: 'moneytrain2',
            moneyTrain3: 'moneytrain3',
            theGreatPigsby: 'thegreatpigsby',
            snakeArena: 'snakearena',
            stampedeFury: 'stampede-fury',
            reelinnRockin: 'reelin-n-rockin',
        };
        if (gameName in GAME_ID_MAPPINGS) {
            return `${gameName},${GAME_ID_MAPPINGS[gameName]}`;
        }
        const originalGameId = Object.keys(GAME_ID_MAPPINGS).find(key => GAME_ID_MAPPINGS[key] === gameName);
        if (originalGameId) {
            return `${gameName},${originalGameId}`;
        }
        return gameName;
    }

    listAllSlotGames(): Promise<SlotGame[]> {
        return this.http.doGet<SlotGame[]>(`/slots/casino-games`).then((games) => {
            const gamesMigratedToGap = [
                "stampedeFury",
                "reelinnRockin",
            ];
            return games.filter((cg) => {
                return cg.provider !== "In House (Table Game)" && gamesMigratedToGap.indexOf(cg.id) == -1
            })
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/service/slots/history/SlotSpinHistoryService.tsx